/**
 * FormTextField
 * -------------
 * A thin wrapper around Polaris TextField that connects to React Hook Form
 * via useFormContext + useController.
 *
 * Components like this are COMPLETELY UNAWARE of A/B testing.
 * All they care about is the `name` prop which must match a key in the form.
 */

import type { TextFieldProps } from "@shopify/polaris";
import { Icon, TextField, Tooltip } from "@shopify/polaris";
import { useController, useFormContext, type FieldValues, type Path } from "react-hook-form";
import { InfoIcon } from "@shopify/polaris-icons";
import { useVariantFieldDisabled } from "./context/VariantFieldContext";

export interface FormTextFieldProps<TFieldValues extends FieldValues = FieldValues> extends Omit<
    TextFieldProps,
    "value" | "onChange" | "onBlur" | "error" | "autoComplete"
> {
    name: Path<TFieldValues> | (string & NonNullable<unknown>);
    label: string;
    helpText?: string;
    autoComplete?: string;
    tooltipContent?: string;
    disabled?: boolean;
    /** Override the value read from RHF (e.g. for array-item fields not registered as a real path). */
    value?: string;
    /** Override the write to RHF — receives the raw new value; call your own transform + field.onChange/setValue. */
    onChange?: (value: string, field: ReturnType<typeof useController<TFieldValues>>["field"]) => void;
    /** Override onBlur (e.g. to reset-to-default or fire a legacy submit callback). */
    onBlur?: (field: ReturnType<typeof useController<TFieldValues>>["field"]) => void;
    /** Override the error shown (e.g. for custom local-state validation instead of/in addition to schema validation). */
    error?: string | boolean;
}

export function FormTextField<TFieldValues extends FieldValues = FieldValues>({
    name,
    label,
    helpText,
    tooltipContent,
    autoComplete = "off",
    disabled,
    value,
    onChange,
    onBlur,
    error,
    ...rest
}: FormTextFieldProps<TFieldValues>) {
    const { control } = useFormContext<TFieldValues>();

    const {
        field,
        fieldState: { error: fieldError },
    } = useController<TFieldValues>({ control, name: name as any });

    const isAbDisabled = useVariantFieldDisabled(name as string);

    return (
        <TextField
            {...rest}
            label={
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                    }}
                >
                    {label}
                    {tooltipContent && (
                        <Tooltip content={tooltipContent} width="wide">
                            <Icon source={InfoIcon} tone="subdued" />
                        </Tooltip>
                    )}
                </div>
            }
            autoComplete={autoComplete}
            value={value !== undefined ? value : String(field.value ?? "")}
            onChange={(val) => (onChange ? onChange(val, field) : field.onChange(val))}
            onBlur={() => (onBlur ? onBlur(field) : field.onBlur())}
            error={error !== undefined ? error : fieldError?.message}
            helpText={helpText}
            disabled={disabled || isAbDisabled}
        />
    );
}
